// CB:BEGIN dev-nav-entries
/**
 * Dev-only navigation list: canonical ROUTES plus screens handled in App / AdminDashboard
 * that are not represented as static slugs in routes.ts.
 */
import { ROUTES } from './routes';

export interface DevNavEntry {
  key: string;
  label: string;
}

function slugToPath(slug: string): string {
  if (slug === 'home') return '/';
  return `/${slug}`;
}

/** One dev row per screen; first static slug wins for the path shown in the label. */
export function devNavEntriesFromRoutes(): DevNavEntry[] {
  const seen = new Set<string>();
  const out: DevNavEntry[] = [];
  for (const r of ROUTES) {
    if (r.slug.includes(':')) continue;
    if (seen.has(r.screen)) continue;
    seen.add(r.screen);
    out.push({
      key: r.screen,
      label: `${slugToPath(r.slug)} · ${r.title}`,
    });
  }
  return out;
}

/**
 * Screens reachable in the app but missing from ROUTES (dynamic paths, admin sub-pages, previews).
 */
const DEV_NAV_EXTRA: DevNavEntry[] = [
  { key: 'mobile-landing', label: '— · Mobile landing (layout preview)' },
  { key: 'project-milestones', label: '— · Project milestones' },
  { key: 'settings', label: '— · Settings' },
  { key: 'contractor-payments', label: '— · Contractor payments' },
  { key: 'contractor-active-projects', label: '— · Contractor active projects' },
  { key: 'contractor-project-milestones', label: '— · Contractor project milestones' },
  { key: 'serviced-zips', label: '— · Serviced zips' },
  { key: 'admin-tools', label: '/admin · Admin tools (alias)' },
  { key: 'admin-users', label: '— · Admin users' },
  { key: 'admin-users-edit', label: '— · Admin user edit' },
  { key: 'admin-projects', label: '— · Admin projects (legacy)' },
  { key: 'admin-project-detail', label: '— · Admin project detail' },
  { key: 'homeowner-dashboard', label: '— · Homeowner dashboard (alias)' },
  { key: 'admin-homeowner-detail', label: '— · Admin homeowner detail' },
  { key: 'admin-contractor-detail', label: '— · Admin contractor detail' },
  { key: 'admin-edit-homeowner-dashboard', label: '— · Admin edit homeowner' },
  { key: 'admin-edit-contractor-dashboard', label: '— · Admin edit contractor' },
  { key: 'admin-realtors', label: '/admin/realtors · Admin realtors' },
  { key: 'admin-reports', label: '— · Admin reports' },
  { key: 'admin-audit-logs', label: '— · Admin audit logs' },
  { key: 'report', label: '— · Report (mock)' },
  { key: 'reports', label: '— · Reports (alias)' },
  { key: 'not-found', label: '— · 404 Not found' },
  { key: 'contractor-license-verification', label: '— · Contractor license verification' },
  { key: 'contractor-insurance-upload', label: '— · Contractor insurance upload' },
  { key: 'realtor-quote-detail', label: '/realtor/quotes/:id · Realtor quote detail' },
  { key: 'realtor-client-portal', label: '/realtor/portal/:token · Realtor client portal' },
  { key: 'admin-project-cockpit', label: '/admin/projects/:id · Admin project cockpit' },
  { key: 'admin-kpi-tracker', label: '— · Admin KPI tracker' },
  { key: 'admin-contractor-verification', label: '— · Admin contractor verification' },
  { key: 'admin-documents', label: '— · Admin documents' },
  { key: 'admin-project-priority', label: '— · Admin project priority' },
  { key: 'admin-questionnaire-management', label: '— · Admin questionnaire' },
  { key: 'admin-license-verification', label: '— · Admin license verification' },
  { key: 'admin-insurance-management', label: '— · Admin insurance' },
  { key: 'admin-performance-monitoring', label: '— · Admin performance monitoring' },
  { key: 'admin-stripe-status-tracking', label: '— · Admin Stripe status' },
  { key: 'admin-revenue-kpi', label: '— · Admin revenue KPI' },
  { key: 'admin-contractor-performance', label: '— · Admin contractor performance' },
  { key: 'admin-project-completion', label: '— · Admin project completion' },
  { key: 'admin-customer-satisfaction', label: '— · Admin customer satisfaction' },
  { key: 'admin-manage', label: '— · Admin manage' },
  { key: 'admin-reviews', label: '— · Admin reviews' },
  { key: 'admin-help', label: '— · Admin help' },
  { key: 'admin-support-requests', label: '/admin/support-requests · Admin support requests' },
  { key: 'eagleview-sandbox', label: '— · EagleView sandbox' },
  { key: 'welcome-email', label: '— · Email preview · Welcome' },
  { key: 'bids-in-email', label: '— · Email preview · Bids in' },
  { key: 'payment-confirmed-email', label: '— · Email preview · Payment confirmed' },
  { key: 'email-report-ordered-preview', label: '— · Email preview · Report ordered' },
  { key: 'email-report-ready-preview', label: '— · Email preview · Report ready' },
  { key: 'email-bid-accepted-preview', label: '— · Email preview · Bid accepted' },
  { key: 'email-start-date-set-preview', label: '— · Email preview · Start date set' },
  { key: 'email-start-reminder-preview', label: '— · Email preview · Start reminder' },
  { key: 'email-out-of-scope-preview', label: '— · Email preview · Out of scope' },
  { key: '__clerk/diagnostics/ui', label: '— · Clerk diagnostics UI' },
];

export function getDevNavEntries(): DevNavEntry[] {
  const fromRoutes = devNavEntriesFromRoutes();
  const keys = new Set(fromRoutes.map((e) => e.key));
  const extras = DEV_NAV_EXTRA.filter((e) => !keys.has(e.key));
  const merged = [...fromRoutes, ...extras];
  merged.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  return merged;
}
// CB:END dev-nav-entries
